import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { CheckCircle, HelpCircle, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import { message } from "antd";
import { device } from "../lib/breakpoints";
import { Button } from "../components/ui";
import { APP_NAME, PERFORMANCE_FEE_DISCLAIMER } from "../helpers/constants";
import { sendContactForm } from "../helpers/contact-form";
import { trackPageViewOnce, trackPortalCta } from "../lib/siteActivity";
import HoneypotField from "../components/security/HoneypotField";
import TurnstileWidget, { isTurnstileRequired } from "../components/security/TurnstileWidget";

const MESSAGE_MAX_LENGTH = 2000;

export default function Contact() {
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("");
  const [formMessage, setFormMessage] = useState("");
  const [websiteUrlHp, setWebsiteUrlHp] = useState("");
  const [turnstileToken, setTurnstileToken] = useState(undefined);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const turnstileRequired = isTurnstileRequired();

  const {
    control,
    formState: { isValid, errors },
    reset,
  } = useForm({
    mode: "onTouched",
    defaultValues: {
      agreedToTerms: false,
    },
  });

  useEffect(() => {
    window.scroll(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    trackPageViewOnce("/contact", "Contact");
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (turnstileRequired && !turnstileToken) {
      message.error("Please complete the security check before sending.");
      return;
    }
    setIsSubmitting(true);

    const data = {
      name,
      email,
      subject,
      message: formMessage,
      website_url_hp: websiteUrlHp,
      "cf-turnstile-response": turnstileToken,
    };

    try {
      const result = await sendContactForm(data);
      if (result.success) {
        message.success("Thanks for contacting us! Check your inbox for a confirmation.");
        setSuccessfullySubmitted(true);
        setName("");
        setEmail("");
        setSubject("");
        setFormMessage("");
        setWebsiteUrlHp("");
        setTurnstileToken(undefined);
        reset();
      }
    } catch (err) {
      message.error(err?.message ?? "Could not send your message. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const faqs = [
    {
      question: "How quickly will you respond to my inquiry?",
      answer:
        "We typically respond to all inquiries within 24 hours during business days via email.",
    },
    {
      question: "What information should I include in my message?",
      answer:
        "Include your company name, county jurisdiction, and whether you have a business personal property assessment notice and equipment asset ledger available. We audit machinery and equipment only, not real estate or buildings.",
    },
    {
      question: "Do you offer phone consultations?",
      answer:
        "Yes. Indicate your preference in the contact form and we will arrange a call to discuss your equipment assessment and potential business personal property tax savings.",
    },
    {
      question: "Can you help with properties in different cities?",
      answer:
        "We support business personal property tax audits in counties where we have depreciation schedule data. Share your operating location and equipment profile and we will confirm coverage.",
    },
  ];

  return (
    <Container>
      {/* Hero Section */}
      <HeroSection>
        <SectionContainer>
          <HeroContent>
            <HeroTitle>Get in Touch</HeroTitle>
            <HeroSubtitle>
              Questions about business personal property tax audits, equipment assessments, or our
              35% performance-fee model? We help operators lower equipment tax bills with zero
              upfront retainers.
            </HeroSubtitle>
          </HeroContent>
        </SectionContainer>
      </HeroSection>

      {/* Contact Form Section */}
      <FormSection>
        <SectionContainer>
          <FormIntro>
            <FormTitle>Send us a Message</FormTitle>
            <FormSubtitle>
              Fill out the form below and we'll get back to you as soon as possible.
            </FormSubtitle>
            <FeatureList>
              <FeatureItem>
                <CheckCircle className="h-5 w-5" />
                <span>Response within 24 hours</span>
              </FeatureItem>
              <FeatureItem>
                <CheckCircle className="h-5 w-5" />
                <span>Expert tax guidance</span>
              </FeatureItem>
              <FeatureItem>
                <CheckCircle className="h-5 w-5" />
                <span>No obligation consultation</span>
              </FeatureItem>
            </FeatureList>
          </FormIntro>

          <FormCard>
            {successfullySubmitted ? (
              <SuccessContainer>
                <SuccessIcon>
                  <CheckCircle className="h-12 w-12" />
                </SuccessIcon>
                <SuccessTitle>Message Sent Successfully!</SuccessTitle>
                <SuccessMessage>
                  Thank you for contacting us. We'll get back to you within 24 hours during business
                  days.
                </SuccessMessage>
                <Button variant="outline" onClick={() => setSuccessfullySubmitted(false)}>
                  Send Another Message
                </Button>
              </SuccessContainer>
            ) : (
              <ContactForm onSubmit={handleSubmit}>
                <HoneypotField
                  id="contact_website_url_hp"
                  value={websiteUrlHp}
                  onChange={(e) => setWebsiteUrlHp(e.target.value)}
                />
                <FormRow>
                  <FormField>
                    <FormLabel>Full Name *</FormLabel>
                    <FormInput
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your full name"
                      required
                    />
                  </FormField>
                  <FormField>
                    <FormLabel>Email Address *</FormLabel>
                    <FormInput
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your@email.com"
                      required
                    />
                  </FormField>
                </FormRow>

                <FormField>
                  <FormLabel>Subject *</FormLabel>
                  <FormInput
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="How can we help you?"
                    required
                  />
                </FormField>

                <FormField>
                  <FormLabel>Message *</FormLabel>
                  <FormTextarea
                    value={formMessage}
                    onChange={(e) => setFormMessage(e.target.value.slice(0, MESSAGE_MAX_LENGTH))}
                    placeholder="Tell us about your business personal property tax audit needs..."
                    rows={5}
                    maxLength={MESSAGE_MAX_LENGTH}
                    required
                  />
                  <MessageCounter aria-live="polite">
                    {formMessage.length}/{MESSAGE_MAX_LENGTH}
                  </MessageCounter>
                </FormField>

                <TermsSection>
                  <Controller
                    name="agreedToTerms"
                    control={control}
                    rules={{
                      required: "You must agree to the terms of service",
                    }}
                    render={({ field }) => (
                      <CheckboxContainer>
                        <FormCheckbox
                          type="checkbox"
                          checked={field.value}
                          onChange={field.onChange}
                        />
                        <CheckboxLabel>
                          I agree to the{" "}
                          <Link to="/terms-of-use" target="_blank">
                            Terms of Service
                          </Link>{" "}
                          and{" "}
                          <Link to="/privacy" target="_blank">
                            Privacy Policy
                          </Link>
                          *
                        </CheckboxLabel>
                      </CheckboxContainer>
                    )}
                  />
                </TermsSection>

                <TurnstileWidget onTokenChange={setTurnstileToken} />

                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={
                    !isValid ||
                    isSubmitting ||
                    !name ||
                    !email ||
                    !subject ||
                    !formMessage ||
                    (turnstileRequired && !turnstileToken)
                  }
                  style={{ width: "100%" }}
                >
                  {isSubmitting ? "Sending..." : "Send Message"}
                </Button>
              </ContactForm>
            )}
          </FormCard>
        </SectionContainer>
      </FormSection>

      {/* FAQ Section */}
      <FAQSection>
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>Frequently Asked Questions</SectionTitle>
            <SectionSubtitle>
              Quick answers to common questions about contacting our support team
            </SectionSubtitle>
          </SectionHeader>

          <FAQGrid>
            {faqs.map((faq, index) => (
              <FAQAccordionItem key={index} $isOpen={openFaqIndex === index}>
                <FAQAccordionHeader
                  type="button"
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  aria-expanded={openFaqIndex === index}
                >
                  <FAQQuestion>
                    <HelpCircle className="h-5 w-5" />
                    {faq.question}
                  </FAQQuestion>
                  <FAQChevron $isOpen={openFaqIndex === index}>
                    <ChevronDown />
                  </FAQChevron>
                </FAQAccordionHeader>
                <FAQAccordionBody $isOpen={openFaqIndex === index}>
                  <FAQAnswer className="text-left text-slate-600 dark:text-slate-400 text-sm leading-relaxed pt-2 pb-4 px-1">
                    {faq.answer}
                  </FAQAnswer>
                </FAQAccordionBody>
              </FAQAccordionItem>
            ))}
          </FAQGrid>
        </SectionContainer>
      </FAQSection>

      {/* CTA Section */}
      <CTASection>
        <SectionContainer>
          <CTAContent>
            <CTATitle>Ready to run your free audit?</CTATitle>
            <CTASubtitle>
              Create a secure corporate portal account to upload your county notice and equipment
              asset ledger and access your secure diagnostic portal with {APP_NAME}.{" "}
              {PERFORMANCE_FEE_DISCLAIMER}
            </CTASubtitle>
            <CTAButtons>
              <Link to="/?triggerSignup=true" onClick={() => trackPortalCta("contact-cta")}>
                <CTAButton variant="primary" size="lg">
                  Request Access
                </CTAButton>
              </Link>
              <Link to="/pricing">
                <CTAOutlineButton variant="outline" size="lg">
                  View Pricing
                </CTAOutlineButton>
              </Link>
            </CTAButtons>
          </CTAContent>
        </SectionContainer>
      </CTASection>
    </Container>
  );
}

// Styled Components
const Container = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  background: linear-gradient(to bottom right, #1a4a73, #0d9488);
  color: white;
  padding: 80px 0;

  @media (${device.mobile}) {
    padding: 60px 0;
  }
`;

const SectionContainer = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px;
  width: 100%;
  box-sizing: border-box;

  @media (min-width: 640px) {
    padding: 0 24px;
  }

  @media (min-width: 1024px) {
    padding: 0 32px;
  }
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 768px;
  margin: 0 auto;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 24px;

  @media (${device.mobile}) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;

  @media (${device.mobile}) {
    font-size: 1.125rem;
  }
`;

const FormSection = styled.section`
  padding: 80px 0;
  background: #f7f9fb;

  @media (${device.mobile}) {
    padding: 60px 0;
  }
`;

const FormIntro = styled.div`
  text-align: center;
  max-width: 640px;
  margin: 0 auto 40px;

  @media (min-width: 768px) {
    margin-bottom: 48px;
  }
`;

const FormTitle = styled.h2`
  font-size: 2.25rem;
  font-weight: 700;
  color: #1f1f1f;
  margin-bottom: 16px;

  @media (${device.mobile}) {
    font-size: 2rem;
  }
`;

const FormSubtitle = styled.p`
  font-size: 1.125rem;
  color: #4a5568;
  margin-bottom: 32px;
  line-height: 1.6;
`;

const FeatureList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
    flex-wrap: wrap;
    gap: 24px;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1rem;

  svg {
    color: #059669;
    flex-shrink: 0;
  }
`;

const FormCard = styled.div`
  background: white;
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  max-width: 720px;
  margin: 0 auto;

  @media (${device.mobile}) {
    padding: 24px;
  }
`;

const ContactForm = styled.form`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;

  @media (min-width: 640px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FormLabel = styled.label`
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const FormInput = styled.input`
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: #1a4a73;
    box-shadow: 0 0 0 3px rgba(46, 95, 155, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const FormTextarea = styled.textarea`
  padding: 12px 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  outline: none;
  -webkit-tap-highlight-color: transparent;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
  resize: vertical;
  min-height: 120px;

  &:focus,
  &:focus-visible {
    outline: none;
    border-color: #1a4a73;
    box-shadow: 0 0 0 3px rgba(46, 95, 155, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const MessageCounter = styled.div`
  align-self: flex-end;
  margin-top: -2px;
  font-size: 0.75rem;
  line-height: 1;
  color: #9ca3af;
  font-variant-numeric: tabular-nums;
`;

const TermsSection = styled.div`
  margin-top: 8px;
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const FormCheckbox = styled.input`
  margin-top: 4px;
  width: 16px;
  height: 16px;
  accent-color: #1a4a73;
`;

const CheckboxLabel = styled.label`
  font-size: 0.875rem;
  color: #6b7280;
  line-height: 1.5;

  a {
    color: #1a4a73;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const SuccessContainer = styled.div`
  text-align: center;
  padding: 40px 20px;
`;

const SuccessIcon = styled.div`
  color: #059669;
  margin-bottom: 24px;
  display: flex;
  justify-content: center;
`;

const SuccessTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f1f1f;
  margin-bottom: 12px;
`;

const SuccessMessage = styled.p`
  color: #6b7280;
  margin-bottom: 32px;
  line-height: 1.6;
`;

const FAQSection = styled.section`
  padding: 80px 0;
  background: white;

  @media (${device.mobile}) {
    padding: 60px 0;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 64px;
`;

const SectionTitle = styled.h2`
  font-size: 2.25rem;
  font-weight: 700;
  color: #1f1f1f;
  margin-bottom: 16px;
`;

const SectionSubtitle = styled.p`
  font-size: 1.125rem;
  color: #4a5568;
  max-width: 600px;
  margin: 0 auto;
`;

const FAQGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 720px;
  margin: 0 auto;
`;

const FAQAccordionItem = styled.div`
  background: #f9fafb;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
`;

const FAQAccordionHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  text-align: left;
  padding: 20px 24px;
  background: none;
  border: none;
  cursor: pointer;
  font: inherit;
  color: inherit;
  transition: background 0.2s;

  &:hover {
    background: #f3f4f6;
  }
`;

const FAQQuestion = styled.span`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f1f1f;
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    color: #1a4a73;
    flex-shrink: 0;
  }
`;

const FAQChevron = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #4a5568;
  transition: transform 0.2s;
  ${(props) => props.$isOpen && "transform: rotate(180deg);"}
`;

const FAQAccordionBody = styled.div`
  overflow: hidden;
  transition: max-height 0.25s ease-out;
  max-height: ${(props) => (props.$isOpen ? "500px" : "0")};
`;

const FAQAnswer = styled.p`
  color: #6b7280;
  line-height: 1.6;
  margin: 0;
  padding: 0 24px 20px 24px;
  text-align: left;
`;

const CTASection = styled.section`
  background: #1a4a73;
  padding: 80px 0;

  @media (${device.mobile}) {
    padding: 60px 0;
  }
`;

const CTAContent = styled.div`
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 2.25rem;
  font-weight: 700;
  color: white;
  margin-bottom: 16px;

  @media (${device.mobile}) {
    font-size: 2rem;
  }
`;

const CTASubtitle = styled.p`
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.125rem;
  margin-bottom: 32px;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: center;

  @media (min-width: 640px) {
    flex-direction: row;
  }
`;

const CTAButton = styled(Button)`
  background: white !important;
  color: #1a4a73 !important;
  border: 1px solid white;

  &:hover {
    background: rgba(255, 255, 255, 0.9) !important;
    color: #1a4a73 !important;
  }
`;

const CTAOutlineButton = styled(Button)`
  border: 1px solid white;
  color: white;
  background: transparent;

  &:hover {
    background: white;
    color: #1a4a73;
  }
`;
